// src/App.jsx
import React, { useState, useEffect, useRef, useCallback, startTransition } from 'react';
import { supabase } from './config/supabase';
import { debounce } from './utils/debounce';
import TiptapEditor from './components/TiptapEditor';
import LoadingSpinner from './components/LoadingSpinner';
import ErrorMessage from './components/ErrorMessage';
import ToolbarMenu from './components/ToolbarMenu';
import InlineToolPicker from './components/InlineToolPicker';
import LessonNotesSidebar from './components/LessonNotesSidebar';
import EventDetailsPanel from './components/EventDetailsPanel';
import ParticipantsAttendanceBar from './components/ParticipantsAttendanceBar';
import LessonHistoryPanel from './components/LessonHistoryPanel';
import { AnimatePresence } from 'framer-motion';
// ⬆ panels will receive a new `variant="inline"` prop
import IndividualNotesDrawer from './components/IndividualNotesDrawer';
import './App.css';
import StickerPickerModal from './components/StickerPickerModal';

const EditorSkeleton = ({ withSidebar, withEventPanel }) => (
  <div className={`editor-container ${withSidebar ? 'with-sidebar' : ''} ${withEventPanel ? 'with-event-panel' : ''}`}>
    <div className="p-4">
      <div className="animate-pulse mb-4">
        <div className="h-6 w-56 bg-gray-200 rounded" />
      </div>
      <div className="animate-pulse flex items-center gap-2 mb-4">
        <div className="h-8 w-20 bg-gray-200 rounded" />
        <div className="h-8 w-28 bg-gray-200 rounded" />
        <div className="h-8 w-10 bg-gray-200 rounded" />
        <div className="h-8 w-10 bg-gray-200 rounded" />
      </div>
      <div className="space-y-3">
        <div className="animate-pulse h-4 w-3/4 bg-gray-200 rounded" />
        <div className="animate-pulse h-4 w-5/6 bg-gray-200 rounded" />
        <div className="animate-pulse h-4 w-2/3 bg-gray-200 rounded" />
        <div className="animate-pulse h-4 w-4/6 bg-gray-200 rounded" />
        <div className="animate-pulse h-4 w-1/2 bg-gray-200 rounded" />
      </div>
      <div className="mt-8 space-y-3">
        <div className="animate-pulse h-5 w-40 bg-gray-200 rounded" />
        <div className="animate-pulse h-24 w-full bg-gray-200 rounded-xl" />
      </div>
    </div>
  </div>
);

// Tailwind "lg" breakpoint by default (desktop layout unchanged above lg)
function useIsMobile(breakpointPx = 1024) {
  const [isMobile, setIsMobile] = useState(() => window.innerWidth < breakpointPx);
  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth < breakpointPx);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, [breakpointPx]);
  return isMobile;
}

function App() {
  const [note, setNote] = useState(null);
  const [lessonsParticipantId, setLessonsParticipantId] = useState(null);
  const [participants, setParticipants] = useState([]);
  const [participantMasterNameById, setParticipantMasterNameById] = useState({}); // master_id -> name
  const [individualStudentNotes, setIndividualStudentNotes] = useState([]); // rows from lesson_note_student_notes
  const [groupId, setGroupId] = useState(null);

  const [appBooting, setAppBooting] = useState(true);
  const [noteLoading, setNoteLoading] = useState(false);
  const [error, setError] = useState(null);

  const [currentUserId, setCurrentUserId] = useState(null);
  const [savingStatus, setSavingStatus] = useState('');
  const [userIds, setUserIds] = useState([]);
  const [tools, setTools] = useState([]);
  const [showToolPicker, setShowToolPicker] = useState(false);
  const [savedSelection, setSavedSelection] = useState(null);
  const [lastAddedToolId, setLastAddedToolId] = useState(null);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [studentId, setStudentId] = useState(null);
  const [masterStudentId, setMasterStudentId] = useState(null);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [showIndividualNotes, setShowIndividualNotes] = useState(false);
  const [showHistory, setShowHistory] = useState(false);
  // only one panel should be visible at once
  const [featureMap, setFeatureMap] = useState({}); // entitlement code -> boolean
  const [entitlementsLoading, setEntitlementsLoading] = useState(false);
  const [upgradePrompt, setUpgradePrompt] = useState({ open: false, message: '' });
  const [studentName, setStudentName] = useState('');
  const [sessionTokens, setSessionTokens] = useState({ accessToken: null, refreshToken: null });

  const [pendingNoteId, setPendingNoteId] = useState(null);
  const [hubReturnTo, setHubReturnTo] = useState(null); // parent hub back-link (validated + persisted)

  const initialTokensRef = useRef({ accessToken: null, refreshToken: null });
  const editorRef = useRef(null);
  const noteContentRef = useRef(null);
  const loadTokenRef = useRef(0);

  // --- Robust autosave state ---
  const saveSeqRef = useRef(0);          // increments for each requested save
  const latestWantedRef = useRef(null);  // { seq, noteId, content, plainText }
  const saveInFlightRef = useRef(false);
  const lastCommittedSeqRef = useRef(0); // last seq we successfully committed
  const lastSavedHashRef = useRef('');   // avoid re-saving identical content

  const draftKeyFor = (noteId) => `mms_lesson_note_draft:${noteId}`;
  const safeJsonStringify = (v) => {
    try { return JSON.stringify(v); } catch { return ''; }
  };

  const persistDraft = (noteId, content) => {
    if (!noteId) return;
    try {
      localStorage.setItem(
        draftKeyFor(noteId),
        JSON.stringify({ content, savedAt: Date.now() })
      );
    } catch {}
  };

  const clearDraft = (noteId) => {
    if (!noteId) return;
    try { localStorage.removeItem(draftKeyFor(noteId)); } catch {}
  };

  const sleep = (ms) => new Promise(r => setTimeout(r, ms));
  const withRetries = async (fn, { retries = 3 } = {}) => {
    let attempt = 0;
    while (true) {
      try {
        return await fn();
      } catch (e) {
        const code = e?.code || e?.error?.code;
        // Only retry statement timeout (57014)
        if (code !== '57014' || attempt >= retries) throw e;
        attempt += 1;
        // exponential backoff with jitter
        const backoff = Math.min(2000, 250 * (2 ** (attempt - 1)));
        const jitter = Math.floor(Math.random() * 150);
        await sleep(backoff + jitter);
      }
    }
  };

  const flushLatestSave = useCallback(async () => {
    if (saveInFlightRef.current) return;
    const wanted = latestWantedRef.current;
    if (!wanted?.noteId || !wanted?.content) return;
    saveInFlightRef.current = true;
    const startedSeq = wanted.seq;

    try {
      setSavingStatus('saving');

      const { noteId, seq, content, plainText } = wanted;
      const contentHash = safeJsonStringify(content);
      if (!contentHash || contentHash === lastSavedHashRef.current) {
        // nothing new
        clearDraft(noteId);
        setSavingStatus('saved');
        setTimeout(() => setSavingStatus(''), 1000);
        return;
      }

      await withRetries(async () => {
        const { data, error } = await supabase.rpc('save_lesson_note_v1', {
          p_note_id: noteId,
          p_content: content,
          p_plain_text: plainText,
        });
        if (error) throw error;
        return data;
      }, { retries: 3 });

      // If a newer save request arrived while we were saving, don't mark as saved yet; loop again.
      if (latestWantedRef.current?.seq !== seq) return;

      lastSavedHashRef.current = contentHash;
      lastCommittedSeqRef.current = seq;
      clearDraft(noteId);

      setSavingStatus('saved');
      setTimeout(() => setSavingStatus(''), 1500);
    } catch (e) {
      console.error('Error saving note:', e);
      setSavingStatus('error');
      // Keep local draft so user can recover.
    } finally {
      saveInFlightRef.current = false;
      // Only run again if a newer save request arrived while we were saving.
      const pending = latestWantedRef.current;
      if (pending?.seq && pending.seq !== startedSeq) {
        Promise.resolve().then(() => {
          if (!saveInFlightRef.current) flushLatestSave();
        });
      }
    }
  }, []);

  // Sticker modal state (Notes app listens directly now)
  const [stickerModalOpen, setStickerModalOpen] = useState(false);
  const [stickerNoteBlockId, setStickerNoteBlockId] = useState(null);
  const [stickerEarnedFor, setStickerEarnedFor] = useState(null);
  const [activeSticker, setActiveSticker] = useState(null); // optional: to animate a preview like before

  // Mobile UI state (safe: desktop layout unchanged)
  const isMobile = useIsMobile(1024);
  const [mobileSidebarOpen, setMobileSidebarOpen] = useState(false);
  const [mobilePanelOpen, setMobilePanelOpen] = useState(false);

  useEffect(() => {
    initializeApp();
  }, []);

  useEffect(() => {
    const { data: sub } = supabase.auth.onAuthStateChange((_event, sess) => {
      setSessionTokens(prev => ({
        accessToken: sess?.access_token ?? prev.accessToken,
        refreshToken: sess?.refresh_token ?? prev.refreshToken,
      }));
    });
    return () => sub.subscription.unsubscribe();
  }, []);

  useEffect(() => {
    const url = new URL(window.location.href);
    const hinted = url.searchParams.get('title') || window.name;
    if (hinted) {
      const label = hinted.length > 60 ? hinted.slice(0, 57) + '…' : hinted;
      document.title = `${label} — Lesson Notes`;
    }
  }, []);

  useEffect(() => {
    const onCompleted = (e) => {
      const { noteBlockId, earnedFor } = e?.detail || {};
      if (!noteBlockId) return;
      setStickerNoteBlockId(noteBlockId);
      setStickerEarnedFor(earnedFor || 'Completed activity');
      setStickerModalOpen(true);
    };
    window.addEventListener('mms:toolCompleted', onCompleted);
    return () => window.removeEventListener('mms:toolCompleted', onCompleted);
  }, []);

  // Entitlements: match Teacher app coercion (safe default: false)
  const coerceToBool = (v) => {
    if (typeof v === 'boolean') return v;
    if (typeof v === 'number') return v !== 0;
    if (v == null) return false;
    if (typeof v === 'string') {
      const s = v.trim().toLowerCase();
      if (['1','true','yes','y','on','enabled','enable','active','pro','studio','unlimited'].includes(s)) return true;
      if (['0','false','no','n','off','disabled','disable','inactive','free'].includes(s)) return false;
      return false;
    }
    return false;
  };

  const loadEntitlementsForSchool = useCallback(async (schoolId) => {
    if (!schoolId) {
      setFeatureMap({});
      return;
    }
    try {
      setEntitlementsLoading(true);
      const { data, error } = await supabase
        .from('school_entitlements_v1')
        .select('feature_code,value')
        .eq('school_id', schoolId);
      if (error) throw error;

      const map = {};
      for (const r of (data || [])) {
        if (!r?.feature_code) continue;
        map[r.feature_code] = coerceToBool(r.value);
      }
      setFeatureMap(map);
    } catch (e) {
      console.warn('[Notes] Failed to load school entitlements:', e);
      // safest: no entitlements known → empty map (buildToolItems defaults to "unlocked" when missing)
      setFeatureMap({});
    } finally {
      setEntitlementsLoading(false);
    }
  }, []);

  useEffect(() => {
    const onUpgradeRequired = (e) => {
      const msg = e?.detail?.message || 'Upgrade to unlock all tools.';
      setUpgradePrompt({ open: true, message: msg });
    };
    window.addEventListener('mms:upgradeRequired', onUpgradeRequired);
    return () => window.removeEventListener('mms:upgradeRequired', onUpgradeRequired);
  }, []);

  useEffect(() => {
    if (lastAddedToolId) {
      const toolElement = document.getElementById(`tool-${lastAddedToolId}`);
      if (toolElement) {
        setTimeout(() => {
          toolElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }, 100);
      }
      setLastAddedToolId(null);
    }
  }, [lastAddedToolId, tools]);

  function removeQueryParam(param) {
    const url = new URL(window.location.href);
    if (url.searchParams.has(param)) {
      url.searchParams.delete(param);
      window.history.replaceState(
        null,
        '',
        url.pathname + (url.searchParams.toString() ? `?${url.searchParams.toString()}` : '') + url.hash
      );
    }
  }

  function updateNoteIdInUrl(noteId) {
    const url = new URL(window.location.href);
    url.searchParams.set('note_id', noteId);
    window.history.pushState(null, '', url.toString());
  }

  const initializeApp = async () => {
    try {
      const url = new URL(window.location.href);
      const urlParams = url.searchParams;
      const noteId = urlParams.get('note_id');

      // Detect Parent Hub entry + persist a safe "return_to" target for a Back button.
      // Notes app may later remove/replace URL params; sessionStorage keeps it stable for this tab.
      const source = urlParams.get('source') || null;
      const returnToFromUrl = urlParams.get('return_to') || null;

      const isAllowedHubReturnTo = (value) => {
        if (!value) return false;
        try {
          const u = new URL(value);
          const host = u.hostname;
          // Allow only Hub domains (prod/preview + Netlify)
          if (
            host === 'hub.mymusicstudio.app' ||
            host === 'preview-hub.mymusicstudio.app' ||
            host === 'hub-mymusicstudio.netlify.app' ||
            host === 'preview-hub.mymusicstudio.netlify.app'
          ) return true;

          // Netlify deploy previews, e.g.
          // deploy-preview-123--preview-hub-mymusicstudio.netlify.app
          if (host.endsWith('.netlify.app') && host.includes('hub') && host.includes('mymusicstudio')) {
            return true;
          }

          return false;
        } catch {
          return false;
        }
      };

      if ((source === 'parent' || source === 'student') && isAllowedHubReturnTo(returnToFromUrl)) {
        sessionStorage.setItem('mms_hub_return_to', returnToFromUrl);
        setHubReturnTo(returnToFromUrl);
      } else {
        const stored = sessionStorage.getItem('mms_hub_return_to');
        if (isAllowedHubReturnTo(stored)) setHubReturnTo(stored);
      }

      const accessTokenFromUrl = urlParams.get('access_token') || urlParams.get('token');
      const refreshTokenFromUrl = urlParams.get('refresh_token') || null;

      initialTokensRef.current = {
        accessToken: accessTokenFromUrl,
        refreshToken: refreshTokenFromUrl
      };

      const isNetlify = window.location.hostname.includes('netlify.app');
      if (isNetlify && window.location.pathname === '/view') {
        const newUrl = `${window.location.origin}/?${urlParams.toString()}`;
        window.location.replace(newUrl);
        return;
      }

      if (!noteId) {
        setError({ title: 'Missing Note ID', message: 'Please provide a valid note_id in the URL parameters.' });
        setAppBooting(false);
        return;
      }

      if (url.searchParams.has('token') || url.searchParams.has('access_token') || url.searchParams.has('refresh_token')) {
        removeQueryParam('token');
        removeQueryParam('access_token');
        removeQueryParam('refresh_token');
      }

      if (accessTokenFromUrl && refreshTokenFromUrl) {
        try {
          const { error: sessionError } = await supabase.auth.setSession({
            access_token: accessTokenFromUrl,
            refresh_token: refreshTokenFromUrl,
          });
          if (sessionError) console.error('Failed to restore session:', sessionError.message);
        } catch (sessionErr) {
          console.error('Error during session restoration:', sessionErr);
        }
      }

      const { data: { session: currentSession } } = await supabase.auth.getSession();
      setSessionTokens(prev => ({
        accessToken: currentSession?.access_token ?? initialTokensRef.current.accessToken ?? prev.accessToken ?? null,
        refreshToken: initialTokensRef.current.refreshToken ?? currentSession?.refresh_token ?? prev.refreshToken ?? null
      }));

      const { data: { user }, error: userError } = await supabase.auth.getUser();

      // Public access (no user session)
      if (userError || !user) {
        const { data: publicNote, error: publicNoteError } = await supabase
          .from('lesson_notes')
          .select('*')
          .eq('id', noteId)
          .is('deleted_at', null)
          .single();

        if (publicNoteError || !publicNote) {
          setError({ title: 'Authentication Required', message: 'Please log in to access this lesson note.' });
          setAppBooting(false);
          return;
        }

        setNote(publicNote);
        setStudentId(publicNote.student_id);
        await fetchStudentInfo(publicNote.student_id);
        await fetchTools(publicNote.id);
        // Load school entitlements for gating (safe for public read-only too)
        loadEntitlementsForSchool(publicNote.school_id);

        try {
          const { data: ls } = await supabase
            .from('lessons')
            .select('group_id')
            .eq('id', publicNote.lesson_id)
            .maybeSingle();
          setGroupId(ls?.group_id ?? null);
        } catch (e) {
          console.warn('Could not determine group context (public):', e);
        }

        if (publicNote.student_id) {
          try {
            const { data: lp, error: lpErr } = await supabase
              .from('lessons_participants')
              .select('id')
              .eq('lesson_id', publicNote.lesson_id)
              .eq('student_id', publicNote.student_id)
              .limit(1)
              .maybeSingle();
            if (lpErr) throw lpErr;
            setLessonsParticipantId(lp?.id ?? null);
          } catch {
            setLessonsParticipantId(null);
          }
        } else {
          setLessonsParticipantId(null);
        }

        try {
          const { data: lps, error: lpsErr } = await supabase
            .from('lessons_participants')
            .select('id, student_id')
            .eq('lesson_id', publicNote.lesson_id);
          if (lpsErr) throw lpsErr;

          const ids = (lps ?? []).map(r => r.student_id).filter(Boolean);
          let namesById = {};
          let namesByMasterId = {};
          if (ids.length) {
            const { data: studs, error: studsErr } = await supabase
              .from('students')
              .select('id, name, student_master_id')
              .in('id', ids);
            if (!studsErr) {
              namesById = Object.fromEntries((studs ?? []).map(s => [s.id, s.name || 'Student']));

              // Build master-id -> full_name via students_master_list (canonical display name)
              const masterIds = Array.from(new Set((studs ?? []).map(s => s.student_master_id).filter(Boolean)));
              if (masterIds.length) {
                const { data: sml, error: smlErr } = await supabase
                  .from('students_master_list')
                  .select('id, full_name')
                  .in('id', masterIds);
                if (!smlErr) {
                  namesByMasterId = Object.fromEntries((sml ?? []).map(m => [m.id, m.full_name || 'Student']));
                }
              }
            }
          }
          const mapped = (lps ?? []).map(r => ({
            lessons_participant_id: r.id,
            student_id: r.student_id,
            student_name: namesById[r.student_id] || 'Student'
          }));
          setParticipants(mapped);
          setParticipantMasterNameById(namesByMasterId);
        } catch (e) {
          console.error('Failed to load participants (public):', e);
          setParticipants([]);
          setParticipantMasterNameById({});
        }

        setAppBooting(false);
        return;
      }

      // Authenticated
      // Teachers live in public.users; parents do NOT. (Identity refactor)
      // Teachers: allow edit; Parents: allow read-only (currentUserId stays null).
      const { data: userRecords, error: userRecordsError } = await supabase
        .from('users')
        .select('id')
        .eq('auth_user_id', user.id);

      const teacherUserIds =
        (!userRecordsError && Array.isArray(userRecords) && userRecords.length > 0)
          ? userRecords.map(r => r.id)
          : [];

      if (teacherUserIds.length > 0) {
        setUserIds(teacherUserIds);
        await loadNote(noteId, teacherUserIds);
        setAppBooting(false);
        return;
      }

      // Not a teacher user. Try parent identity (hub world).
        const { data: parentRows, error: parentErr } = await supabase
        .from('parents')
        .select('id')
        .eq('auth_user_id', user.id)
        .limit(1);

        const isParent = !parentErr && Array.isArray(parentRows) && parentRows.length > 0;

        if (isParent) {
        // Parent authenticated: proceed read-only
        setUserIds([]); // no teacher identities
        await loadNote(noteId, []);
        setAppBooting(false);
        return;
        }

        // Not a parent — try student identity.
        // students_master_list has auth_user_id (per schema) and should be RLS "self only".
        const { data: studentRows, error: studentErr } = await supabase
        .from('students_master_list')
        .select('id')
        .eq('auth_user_id', user.id)
        .limit(1);

        const isStudent = !studentErr && Array.isArray(studentRows) && studentRows.length > 0;

        if (isStudent) {
        // Student authenticated: proceed read-only (same as parent UX)
        setUserIds([]); // still not a teacher editor
        await loadNote(noteId, []);
        setAppBooting(false);
        return;
        }

        // Neither parent nor student → block
        setError({
        title: 'User Not Found',
        message: 'Unable to find your user profile. Please contact support.',
        });
        setAppBooting(false);
        return;

    } catch (err) {
      console.error('Error initializing app:', err);
      setError({ title: 'Something Went Wrong', message: 'An unexpected error occurred while loading the lesson note.' });
      setAppBooting(false);
    }
  };

  const fetchStudentInfo = async (sid) => {
    try {
      if (!sid) {
        setStudentName('Group Lesson');
        setMasterStudentId(null);
        return;
      }
      const { data: studentData, error: studentError } = await supabase
        .from('students')
        .select('name, student_master_id')
        .eq('id', sid)
        .single();

      if (studentError) {
        setStudentName('Unknown Student');
        setMasterStudentId(null);
      } else {
        setStudentName(studentData?.name || 'Unknown Student');
        setMasterStudentId(studentData?.student_master_id ?? null);
      }
    } catch {
      setStudentName('Unknown Student');
      setMasterStudentId(null);
    }
  };

  useEffect(() => {
    if (studentName && studentName !== 'Unknown Student') {
      const clean = (s) => s.replace(/\s+/g, ' ').trim();
      const label = clean(studentName).length > 60 ? clean(studentName).slice(0, 57) + '…' : clean(studentName);
      document.title = `${label} — Lesson Notes`;
    }
  }, [studentName]);

  const loadNote = async (noteId, allUserIds = userIds) => {
    const token = ++loadTokenRef.current;
    try {
      const { data: noteData, error: noteError } = await supabase
        .from('lesson_notes')
        .select('*')
        .eq('id', noteId)
        .is('deleted_at', null)
        .single();

      if (loadTokenRef.current !== token) return;

      if (noteError || !noteData) {
        const { data: anyNote } = await supabase
          .from('lesson_notes')
          .select('*')
          .eq('id', noteId)
          .is('deleted_at', null)
          .single();

        if (loadTokenRef.current !== token) return;

        if (anyNote) {
          if (allUserIds.includes(anyNote.teacher_id)) {
            setCurrentUserId(anyNote.teacher_id);
            setNote(anyNote);
            setStudentId(anyNote.student_id);
            await fetchStudentInfo(anyNote.student_id);
            await fetchTools(anyNote.id);
            // Load school entitlements for gating
            loadEntitlementsForSchool(anyNote.school_id);
            return;
          }

          const { data: accessData } = await supabase
            .from('lesson_notes_access')
            .select('*')
            .eq('note_id', noteId)
            .in('user_id', allUserIds)
            .single();

          if (loadTokenRef.current !== token) return;

          if (accessData) {
            setNote(anyNote);
            setStudentId(anyNote.student_id);
            await fetchStudentInfo(anyNote.student_id);
            await fetchTools(anyNote.id);
            // Load school entitlements for gating
            loadEntitlementsForSchool(anyNote.school_id);
            return;
          }
        }

        setError({ title: 'Note Not Found', message: 'The requested lesson note could not be found or you lack permission.' });
        return;
      }

      const isTeacher = allUserIds.includes(noteData.teacher_id);
      setCurrentUserId(isTeacher ? noteData.teacher_id : null);
      setNote(noteData);
      setStudentId(noteData.student_id);
      await fetchStudentInfo(noteData.student_id);
      await fetchTools(noteId);
      // Load school entitlements for gating
      loadEntitlementsForSchool(noteData.school_id);

      try {
        const { data: ls } = await supabase
          .from('lessons')
          .select('group_id')
          .eq('id', noteData.lesson_id)
          .maybeSingle();
        setGroupId(ls?.group_id ?? null);
      } catch {
        setGroupId(null);
      }

      if (noteData.student_id) {
        try {
          const { data: lp } = await supabase
            .from('lessons_participants')
            .select('id')
            .eq('lesson_id', noteData.lesson_id)
            .eq('student_id', noteData.student_id)
            .limit(1)
            .maybeSingle();
          setLessonsParticipantId(lp?.id ?? null);
        } catch {
          setLessonsParticipantId(null);
        }
      } else {
        setLessonsParticipantId(null);
      }

      try {
        const { data: lps } = await supabase
          .from('lessons_participants')
          .select('id, student_id')
          .eq('lesson_id', noteData.lesson_id);
        const ids = (lps ?? []).map(r => r.student_id).filter(Boolean);
        let namesByMasterId = {};
        let namesById = {};
        if (ids.length) {
          const { data: studs } = await supabase
            .from('students')
            .select('id, name, student_master_id')
            .in('id', ids);
          namesById = Object.fromEntries((studs ?? []).map(s => [s.id, s.name || 'Student']));
          
          // Canonical: master-id -> students_master_list.full_name
          const masterIds = Array.from(new Set((studs ?? []).map(s => s.student_master_id).filter(Boolean)));
          if (masterIds.length) {
            const { data: sml, error: smlErr } = await supabase
              .from('students_master_list')
              .select('id, full_name')
              .in('id', masterIds);
            if (!smlErr) {
              namesByMasterId = Object.fromEntries((sml ?? []).map(m => [m.id, m.full_name || 'Student']));
            }
          }
        }
        const mapped = (lps ?? []).map(r => ({
          lessons_participant_id: r.id,
          student_id: r.student_id,
          student_name: namesById[r.student_id] || 'Student'
        }));
        setParticipants(mapped);
        setParticipantMasterNameById(namesByMasterId);
      } catch {
        setParticipants([]);
        setParticipantMasterNameById({});
      }

      try {
        const { data: indiv } = await supabase
          .from('lesson_note_student_notes')
          .select('id, content, student_master_id, updated_at')
          .eq('lesson_note_id', noteId)
          .is('deleted_at', null)
          .order('updated_at', { ascending: false });
        const indivRows = indiv ?? [];
        setIndividualStudentNotes(indivRows);

        // ✅ Ensure names for Individual Notes are resolved by student_master_id via students_master_list.full_name
        // This is intentionally independent of lessons_participants/students lookups (which may be restricted by RLS).
        try {
          const masterIds = Array.from(
            new Set(indivRows.map(r => r.student_master_id).filter(Boolean))
          );
          if (masterIds.length) {
            const { data: sml, error: smlErr } = await supabase
              .from('students_master_list')
              .select('id, full_name')
              .in('id', masterIds);
            if (!smlErr) {
              const namesByMasterId = Object.fromEntries(
                (sml ?? []).map(m => [m.id, m.full_name || 'Student'])
              );
              setParticipantMasterNameById(prev => ({ ...prev, ...namesByMasterId }));
            }
          }
        } catch (e) {
          // Best-effort only; UI will fall back to "Student" if this fails.
          console.warn('Failed to resolve students_master_list names for individual notes:', e);
        }
      } catch {
        setIndividualStudentNotes([]);
      }
    } catch (err) {
      console.error('Error loading note:', err);
      setError({ title: 'Something Went Wrong', message: 'An unexpected error occurred while loading the lesson note.' });
    }
  };

  /**
   * LEGACY: lesson_tools (iframe-based tools)
   * The Notes app now uses TipTap-native tools.
   * This fetch is intentionally disabled to avoid slow parent RLS queries.
   */
  const fetchTools = async (_noteId) => {
    setTools([]);
  };

  const debouncedSave = debounce((content) => {
    if (!note || !currentUserId) return;
    const plainText = extractPlainText(content);

    // Always persist local draft first (catastrophe-proof)
    persistDraft(note.id, content);

    const seq = ++saveSeqRef.current;
    latestWantedRef.current = { seq, noteId: note.id, content, plainText };
    flushLatestSave();
  }, 1000);

  const extractPlainText = (content) => {
    let text = '';
    const traverse = (node) => {
      if (node.text) text += node.text + ' ';
      if (node.content && Array.isArray(node.content)) node.content.forEach(traverse);
    };
    if (content && content.content) content.content.forEach(traverse);
    return text.trim();
  };

  const handleContentUpdate = (content) => {
    if (currentUserId) {
      debouncedSave(content);
    } else {
      console.log('Not saving changes - user not authenticated');
    }
  };

  // Flush pending save when navigating away / closing tab
  useEffect(() => {
    const onBeforeUnload = () => {
      // Best-effort: if we have pending content, keep the draft (already persisted),
      // and attempt a final flush without blocking unload.
      try { flushLatestSave(); } catch {}
    };
    window.addEventListener('beforeunload', onBeforeUnload);
    return () => window.removeEventListener('beforeunload', onBeforeUnload);
  }, [flushLatestSave]);

  const handleToolAdded = useCallback(() => {
    setShowToolPicker(false);
  }, []);
  const handleToolDeleted = useCallback(async () => {
    if (note?.id) await fetchTools(note.id);
  }, [note?.id]);

  const handleOpenToolPicker = () => {
    const sel = editorRef.current?.state?.selection;
    if (sel) setSavedSelection({ from: sel.from, to: sel.to });
    setShowToolPicker(true);
  };

  const handleNoteSelect = (nextNoteId) => {
    if (!nextNoteId || nextNoteId === note?.id) return;
    setPendingNoteId(nextNoteId);
    setSelectedEvent(null);
    setError(null);
    updateNoteIdInUrl(nextNoteId);
    setNoteLoading(true);
    startTransition(() => {
      loadNote(nextNoteId).finally(() => {
        setPendingNoteId(current => (current === nextNoteId ? null : current));
        setNoteLoading(false);
      });
    });
  };

  const handleEventSelect = (evtOrNull) => {
    // open event panel ⇒ close others; clicking same again toggles off
    setShowHistory(false);
    setShowIndividualNotes(false);
    setSelectedEvent(prev => (evtOrNull && prev?.id === evtOrNull.id ? null : evtOrNull));
  };

  const handleToggleSidebar = () => setSidebarCollapsed(v => !v);

  // compute which (if any) panel is active. event wins, else history, else individual (group only)
  const activePanel = selectedEvent
    ? 'event'
    : (showHistory ? 'history' : (showIndividualNotes && !!groupId && !studentId ? 'individual' : null));

  // Is the history panel currently the active one?
  const historyOpen = activePanel === 'history';

  // Track if we're SWITCHING between inline panels (event ↔ history ↔ individual)
  const prevPanelRef = useRef(null);
  const isSwitchingPanels = !!prevPanelRef.current && !!activePanel && prevPanelRef.current !== activePanel;
  useEffect(() => { prevPanelRef.current = activePanel; }, [activePanel]);

  // Mobile: if a panel becomes active, open the mobile sheet automatically.
  useEffect(() => {
    if (!isMobile) return;
    setMobilePanelOpen(!!activePanel);
  }, [isMobile, activePanel]);

  // Helpers: used by mobile UI
  const closeMobileSheets = () => {
    setMobileSidebarOpen(false);
    setMobilePanelOpen(false);
  };

  if (appBooting) {
    return (
      <div className="flex h-screen bg-white">
        {(studentId || groupId) && !isMobile && (
          <LessonNotesSidebar
            currentNoteId={null}
            studentId={studentId}
            groupId={groupId}
            onNoteSelect={() => {}}
            onEventSelect={() => {}}
            selectedEventId={null}
            isCollapsed={sidebarCollapsed}
            onToggleCollapse={handleToggleSidebar}
          />
        )}
        <div className="flex-1 overflow-hidden">
          <EditorSkeleton withSidebar={!!(studentId || groupId) && !isMobile} withEventPanel={false} />
        </div>
      </div>
    );
  }

  if (error) return <ErrorMessage title={error.title} message={error.message} />;

  const isReadOnly = !currentUserId;
  const selectedSidebarNoteId = pendingNoteId ?? note?.id ?? null;
  const isGroupLesson = !!groupId && !studentId;
  const suppressReadOnlyBanner = true;

  const handleBackgroundMouseDown = (e) => {
    if (isReadOnly) return;

    // ✅ Don't steal focus from any portaled modal (e.g., attendance comment modal)
    // React capture handlers run before modal capture handlers, so we must guard here.
    if (e?.target?.closest?.('[data-mms-modal]')) return;

    // ✅ Don't treat interactions with editor UI (bubble menus / toolbars / buttons) as "background" clicks.
    // Otherwise we collapse the selection before TipTap commands run, which hides the bubble menu
    // and prevents formatting from applying.
    const interactive = e?.target?.closest?.(
      [
        'button',
        '[role="button"]',
        'a',
        'input',
        'textarea',
        'select',
        '[contenteditable="false"]',
        // TipTap BubbleMenu uses Tippy under the hood; these wrappers frequently appear.
        '[data-tippy-root]',
        '.tippy-box',
        '.tippy-content',
      ].join(', ')
    );
    if (interactive) return;

    const view = editorRef.current?.view;
    if (!view) return;
    if (view.dom.contains(e.target)) return;
    e.preventDefault();
    const { clientX: left, clientY: top } = e;
    const at = view.posAtCoords?.({ left, top });
    const pos = (at && typeof at.pos === 'number')
      ? at.pos
      : view.state.doc.content.size;
    editorRef.current
      ?.chain()
      .setTextSelection(pos)
      .focus()
      .run();
  };

  return (
    <div className="flex h-screen bg-white">
      {/* DESKTOP SIDEBAR (unchanged) */}
      {(studentId || groupId) && !isMobile && (
        <LessonNotesSidebar
          currentNoteId={selectedSidebarNoteId}
          studentId={studentId}
          groupId={groupId}
          onNoteSelect={handleNoteSelect}
          onEventSelect={handleEventSelect}
          selectedEventId={selectedEvent?.id || null}
          isCollapsed={sidebarCollapsed}
          onToggleCollapse={handleToggleSidebar}
          hideUpcomingEvents={isReadOnly}
        />
      )}

      {/* MOBILE SIDEBAR SHEET (new; does not affect desktop) */}
      {(studentId || groupId) && isMobile && mobileSidebarOpen && (
        <div className="fixed inset-0 z-[60]" role="dialog" aria-modal="true" aria-label="Notes navigation">
          <div
            className="absolute inset-0 bg-black/35"
            onClick={() => setMobileSidebarOpen(false)}
          />
          <div className="absolute left-0 top-0 bottom-0 w-[min(92vw,420px)] bg-white shadow-xl flex flex-col">
            <div className="flex justify-end p-2 border-b border-gray-200">
              <button
                type="button"
                onClick={() => setMobileSidebarOpen(false)}
                className="inline-flex items-center px-3 py-1.5 text-sm rounded-md border border-gray-300 bg-white hover:bg-gray-50 shadow-sm"
                aria-label="Close navigation"
                title="Close"
              >
                ✕
              </button>
            </div>

            <div className="flex-1 overflow-hidden">
              <LessonNotesSidebar
                currentNoteId={selectedSidebarNoteId}
                studentId={studentId}
                groupId={groupId}
                onNoteSelect={(id) => { setMobileSidebarOpen(false); handleNoteSelect(id); }}
                onEventSelect={(evt) => { setMobileSidebarOpen(false); handleEventSelect(evt); }}
                selectedEventId={selectedEvent?.id || null}
                isCollapsed={false}
                onToggleCollapse={() => {}}
                hideUpcomingEvents={isReadOnly}
              />
            </div>
          </div>
        </div>
      )}

      <div className="flex-1 flex flex-col overflow-hidden">
        <div className="flex-1 flex overflow-hidden relative">
          <div className={`flex-1 overflow-y-auto relative ${isMobile ? 'overflow-x-hidden' : ''}`}>
            <div
                className={`editor-container ${(studentId || groupId) ? 'with-sidebar' : ''} ${selectedEvent ? 'with-event-panel' : ''} ${
                    isMobile ? 'max-w-full' : ''
                }`}
                >
              {/* ✅ Nice step (small + safe): Mobile top bar instead of FAB.
                  Desktop action row below remains unchanged and still renders on desktop only. */}
              <div className="lg:hidden sticky top-0 z-40 bg-white border-b border-gray-200">
                <div className="flex items-center justify-between px-3 py-2">
                  <div className="flex items-center gap-2">
                    {(studentId || groupId) && (
                      <button
                        type="button"
                        onClick={() => setMobileSidebarOpen(true)}
                        className="inline-flex items-center px-3 py-1.5 text-sm rounded-md border border-gray-300 bg-white hover:bg-gray-50 shadow-sm"
                        aria-label="Open notes list"
                        title="Open notes list"
                      >
                        ☰
                      </button>
                    )}
                    {hubReturnTo && (
                      <button
                        type="button"
                        onClick={() => window.location.assign(hubReturnTo)}
                        className="inline-flex items-center px-3 py-1.5 text-sm rounded-md border border-gray-300 bg-white hover:bg-gray-50 shadow-sm"
                        title="Back to Family Hub"
                        aria-label="Back to hub"
                      >
                        ← Hub
                      </button>
                    )}
                  </div>

                  <div className="flex items-center gap-2">
                    <button
                      type="button"
                      onClick={() => {
                        setSelectedEvent(null);
                        setShowIndividualNotes(false);
                        setShowHistory(prev => !prev);
                        setMobilePanelOpen(true);
                      }}
                      aria-pressed={historyOpen}
                      className="inline-flex items-center px-3 py-1.5 text-sm rounded-md border border-gray-300 bg-white hover:bg-gray-50 shadow-sm"
                      title={historyOpen ? 'Hide history' : 'Show history'}
                    >
                      {historyOpen ? 'Hide' : 'History'}
                    </button>

                    {currentUserId && isGroupLesson && (
                      <button
                        type="button"
                        onClick={() => {
                          setSelectedEvent(null);
                          setShowHistory(false);
                          setShowIndividualNotes(prev => !prev);
                          setMobilePanelOpen(true);
                        }}
                        className="inline-flex items-center px-3 py-1.5 text-sm rounded-md border border-gray-300 bg-white hover:bg-gray-50 shadow-sm"
                        title="Add individual notes"
                      >
                        Individual
                      </button>
                    )}

                    {activePanel && (
                      <button
                        type="button"
                        onClick={() => setMobilePanelOpen(true)}
                        className="inline-flex items-center px-3 py-1.5 text-sm rounded-md border border-gray-300 bg-white hover:bg-gray-50 shadow-sm"
                        title="Open panel"
                        aria-label="Open panel"
                      >
                        Panel
                      </button>
                    )}
                  </div>
                </div>
              </div>

              {/* DESKTOP TOP ACTION ROW (unchanged; hidden on mobile) */}
              {note && (
                <div className="hidden lg:flex items-center justify-between px-4 pt-3">
                  <div className="flex items-center gap-2">
                    {hubReturnTo && (
                      <button
                        type="button"
                        onClick={() => window.location.assign(hubReturnTo)}
                        className="inline-flex items-center px-3 py-1.5 text-sm rounded-md border border-gray-300 bg-white hover:bg-gray-50 shadow-sm"
                        title="Back to Family Hub"
                        aria-label="Back to hub"
                      >
                        ← Back to hub
                      </button>
                    )}
                  </div>
                  <div className="flex items-center gap-2">
                    <button
                      type="button"
                      onClick={() => {
                        // open history ⇒ close others; toggle if already open
                        setSelectedEvent(null);
                        setShowIndividualNotes(false);
                        setShowHistory(prev => !prev);
                      }}
                      aria-pressed={historyOpen}
                      className="inline-flex items-center px-3 py-1.5 text-sm rounded-md border border-gray-300 bg-white hover:bg-gray-50 shadow-sm"
                      title={historyOpen ? 'Hide history' : 'Show history'}
                    >
                      {historyOpen ? 'Hide history' : 'Show history'}
                    </button>
                    {currentUserId && isGroupLesson && (
                      <button
                        type="button"
                        onClick={() => {
                          setSelectedEvent(null);
                          setShowHistory(false);
                          setShowIndividualNotes(prev => !prev);
                        }}
                        className="inline-flex items-center px-3 py-1.5 text-sm rounded-md border border-gray-300 bg-white hover:bg-gray-50 shadow-sm"
                        title="Add individual notes"
                      >
                        Add individual notes
                      </button>
                    )}
                  </div>
                </div>
              )}

              {isGroupLesson && note?.lesson_id && (
                <ParticipantsAttendanceBar
                  lessonId={note.lesson_id}
                  participants={participants}
                  disabled={!currentUserId}
                />
              )}

              <div
                ref={noteContentRef}
                onMouseDownCapture={handleBackgroundMouseDown}
                style={{
                  cursor: isReadOnly ? 'default' : 'text',
                  // Slightly tighter padding on mobile only (safe; desktop unchanged)
                  padding: isMobile ? '0.75rem' : '1rem',
                  minHeight: '60vh',
                  background: 'white'
                }}
              >
                {note ? (
                  <TiptapEditor
                    content={note.content}
                    onUpdate={handleContentUpdate}
                    featureMap={featureMap}
                    entitlementsLoading={entitlementsLoading}
                    title={note.title || 'Untitled Lesson Note'}
                    suppressReadOnlyBanner={true}
                    belowDividerContent={
                      (isGroupLesson && !currentUserId && individualStudentNotes.length > 0) ? (
                        <div className="px-0">
                          <div className="mt-2 border border-gray-200 rounded-xl bg-gray-50">
                            <div className="px-4 py-2 border-b border-gray-200">
                              <h3 className="text-sm font-semibold text-gray-700">Individual notes</h3>
                            </div>
                            <div className="divide-y divide-gray-200">
                              {individualStudentNotes.map((row) => (
                                <div key={row.id} className="px-4 py-3">
                                  <div className="text-xs text-gray-500 mb-1">
                                    {participantMasterNameById[row.student_master_id] || 'Student'}
                                  </div>
                                  <div className="whitespace-pre-wrap text-sm text-gray-900">
                                    {row.content}
                                  </div>
                                </div>
                              ))}
                            </div>
                          </div>
                        </div>
                      ) : null
                    }
                    savingStatus={savingStatus}
                    readOnly={isReadOnly}
                    editorRef={editorRef}
                    onOpenToolPicker={handleOpenToolPicker}
                    lessonId={note.lesson_id}
                    lessonsParticipantId={lessonsParticipantId}
                    lessonNoteId={note.id}
                    studentId={studentId}
                    masterStudentId={masterStudentId}
                    groupId={groupId}
                    studentName={studentName}
                    lessonDate={note.lesson_datetime || note.created_at}
                    noteContentRef={noteContentRef}
                    placeholder="Start typing your lesson notes…"
                    suppressIndividualAttendance={isGroupLesson}
                    userId={currentUserId}
                    schoolId={note?.school_id ?? null}
                  />
                ) : (
                  <EditorSkeleton withSidebar={!!(studentId || groupId)} withEventPanel={!!selectedEvent} />
                )}
              </div>

              {/* LEGACY: iframe-based LessonTools removed */}

              <StickerPickerModal
                isOpen={stickerModalOpen}
                onClose={() => setStickerModalOpen(false)}
                onSelected={(s) => {
                  setActiveSticker({ image_url: s.image_url, label: s.label });
                }}
                schoolId={note?.school_id ?? null}
                studentId={studentId}
                studentMasterId={masterStudentId}
                lessonNoteId={note?.id ?? null}
                noteBlockId={stickerNoteBlockId}
                earnedFor={stickerEarnedFor}
              />

              <ToolbarMenu
                noteId={note?.id}
                isReadOnly={isReadOnly}
                editor={editorRef.current}
                onToolAdded={handleToolAdded}
                onOpenToolPicker={handleOpenToolPicker}
              />

              <AnimatePresence>
                {showToolPicker && (
                  <InlineToolPicker
                    editor={editorRef.current}
                    savedSelection={savedSelection}
                    onClose={handleToolAdded}
                  />
                )}
              </AnimatePresence>
            </div>

            {noteLoading && (
              <div className="absolute inset-0 bg-white/40 backdrop-blur-[1px] pointer-events-none">
                <div className="absolute top-3 right-4 text-xs text-gray-600 bg-white/80 border border-gray-200 rounded-md px-2 py-1 shadow-sm">
                  Loading…
                </div>
              </div>
            )}
          </div>

          {/* DESKTOP RIGHT COLUMN (unchanged) */}
          {!isMobile && (
            <AnimatePresence>
              {activePanel && (
                <div
                  key={`panel-${activePanel}`}
                  className="h-full w-96 shrink-0 border-l border-gray-200 bg-white relative"
                >
                  {activePanel === 'event' && selectedEvent && (
                    <EventDetailsPanel
                      variant="inline"
                      inlineTransition={isSwitchingPanels ? 'swap' : 'enter'}
                      event={selectedEvent}
                      onClose={() => setSelectedEvent(null)}
                    />
                  )}
                  {activePanel === 'history' && note && (
                    <LessonHistoryPanel
                      variant="inline"
                      inlineTransition={isSwitchingPanels ? 'swap' : 'enter'}
                      currentNoteId={note.id}
                      studentId={studentId || null}
                      groupId={groupId || null}
                      beforeDate={(note.lesson_datetime || note.created_at)}
                      onClose={() => setShowHistory(false)}
                    />
                  )}
                  {activePanel === 'individual' && isGroupLesson && note && (
                    <IndividualNotesDrawer
                      variant="inline"
                      inlineTransition={isSwitchingPanels ? 'swap' : 'enter'}
                      lessonNoteId={note.id}
                      lessonId={note.lesson_id}
                      currentUserId={currentUserId}
                      onClose={() => setShowIndividualNotes(false)}
                    />
                  )}
                </div>
              )}
            </AnimatePresence>
          )}

          {/* MOBILE PANEL SHEET (new; does not affect desktop) */}
          {isMobile && mobilePanelOpen && activePanel && (
            <div className="fixed inset-0 z-[60]" role="dialog" aria-modal="true" aria-label="Details panel">
              <div
                className="absolute inset-0 bg-black/35"
                onClick={() => setMobilePanelOpen(false)}
              />
              <div className="absolute right-0 top-0 bottom-0 w-[min(92vw,420px)] bg-white shadow-xl flex flex-col">
                <div className="flex items-center justify-between p-2 border-b border-gray-200">
                  <div className="text-sm font-medium text-gray-700 px-1">
                    {activePanel === 'event' ? 'Event' : activePanel === 'history' ? 'History' : 'Individual notes'}
                  </div>
                  <button
                    type="button"
                    onClick={() => setMobilePanelOpen(false)}
                    className="inline-flex items-center px-3 py-1.5 text-sm rounded-md border border-gray-300 bg-white hover:bg-gray-50 shadow-sm"
                    aria-label="Close panel"
                    title="Close"
                  >
                    ✕
                  </button>
                </div>

                <div className="flex-1 overflow-y-auto">
                  {activePanel === 'event' && selectedEvent && (
                    <EventDetailsPanel
                      variant="inline"
                      inlineTransition={isSwitchingPanels ? 'swap' : 'enter'}
                      event={selectedEvent}
                      onClose={() => {
                        setMobilePanelOpen(false);
                        setSelectedEvent(null);
                      }}
                    />
                  )}
                  {activePanel === 'history' && note && (
                    <LessonHistoryPanel
                      variant="inline"
                      inlineTransition={isSwitchingPanels ? 'swap' : 'enter'}
                      currentNoteId={note.id}
                      studentId={studentId || null}
                      groupId={groupId || null}
                      beforeDate={(note.lesson_datetime || note.created_at)}
                      onClose={() => {
                        setMobilePanelOpen(false);
                        setShowHistory(false);
                      }}
                    />
                  )}
                  {activePanel === 'individual' && isGroupLesson && note && (
                    <IndividualNotesDrawer
                      variant="inline"
                      inlineTransition={isSwitchingPanels ? 'swap' : 'enter'}
                      lessonNoteId={note.id}
                      lessonId={note.lesson_id}
                      currentUserId={currentUserId}
                      onClose={() => {
                        setMobilePanelOpen(false);
                        setShowIndividualNotes(false);
                      }}
                    />
                  )}
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Upgrade prompt (teacher-only in practice; safe if it renders for anyone) */}
      {upgradePrompt.open && (
        <div
          className="fixed bottom-4 left-4 right-4 z-[11000] max-w-xl mx-auto bg-white border border-orange-200 shadow-lg rounded-xl p-4"
          role="status"
          aria-live="polite"
        >
          <div className="flex items-start justify-between gap-3">
            <div className="min-w-0">
              <div className="text-sm font-semibold text-gray-900">Upgrade required</div>
              <div className="text-sm text-gray-700 mt-1 break-words">
                {upgradePrompt.message}
              </div>
            </div>
            <button
              type="button"
              onClick={() => setUpgradePrompt({ open: false, message: '' })}
              className="text-gray-500 hover:text-gray-700"
              aria-label="Dismiss upgrade prompt"
              title="Dismiss"
            >
              ✕
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

export default App;